import { ProgramItemContent, ProgramItemType } from 'store/programItems';
import { GetProgramItemsByContentIdentifiers } from 'types/services/magnolia/GetProgramItemsByContentIdentifiers';
import { ProgramItemDtoType } from 'types/services/spring/dtos/ProgramItemDto';
import {
	FLUSH_PROGRAM_ITEMS,
	REPLACE_ITEM,
	REPLACE_ITEM_CONTENTS,
	REQUEST_CLOSE,
	REQUEST_CLOSE_SUCCESS,
	REQUEST_COMPLETED,
	REQUEST_COMPLETED_SUCCESS,
	REQUEST_CONTENTS_SUCCESS,
	REQUEST_HEART,
	REQUEST_HEART_SUCCESS,
	REQUEST_MOVE_ITEM,
	REQUEST_MOVE_ITEM_FAILURE,
	REQUEST_MOVE_ITEM_SUCCESS,
	REQUEST_OPTIONAL_WORKOUT_CREATE_CUSTOM,
	REQUEST_POSTPONE,
	REQUEST_POSTPONE_SUCCESS,
	REQUEST_SAVE_TRACKING,
	REQUEST_SAVE_TRACKING_SUCCESS,
	REQUEST_SLOGAN,
	REQUEST_SLOGAN_SUCCESS,
	REQUEST_SWAP_ITEM,
	REQUEST_SWAP_ITEM_FAILURE,
	REQUEST_SWAP_ITEM_SUCCESS,
	REQUEST_TOGGLE_FAVORITE,
	REQUEST_TOGGLE_FAVORITE_FAILURE,
	REQUEST_TOGGLE_FAVORITE_SUCCESS,
	REQUEST_UPDATE_TRACKING,
	REQUEST_UPDATE_TRACKING_SUCCESS,
	REQUEST_WORKOUT_COMPLETED,
	REQUEST_WORKOUT_CUSTOM_UPDATED,
	REQUEST_WORKOUT_CUSTOM_UPDATED_AND_COMPLETE,
	REQUEST_WORKOUT_REPLACE_WITH_CUSTOM,
	SET_ITEM_IS_CUSTOM,
} from './programItems.actionTypes';

export type MeasurementType = {
	weight: string;
	waist: string;
	bodyFat: string;
};

export const actionCreators = {
	doFlushProgramItems: () => ({
		type: FLUSH_PROGRAM_ITEMS,
	}),
	doRequestProgramItemsContentsSuccess: (programItemsContents: GetProgramItemsByContentIdentifiers) => ({
		type: REQUEST_CONTENTS_SUCCESS,
		programItemsContents,
	}),
	doReplaceItem: (programItem: ProgramItemType) => ({
		type: REPLACE_ITEM,
		programItem,
	}),
	doSetItemIsCustom: (programItemId: string) => ({
		type: SET_ITEM_IS_CUSTOM,
		programItemId,
	}),

	doReplaceItemContents: (programItemContents: ProgramItemContent, programItemId: number) => ({
		type: REPLACE_ITEM_CONTENTS,
		programItemContents,
		programItemId,
	}),
	doComplete: (programItemId: string) => ({
		type: REQUEST_COMPLETED,
		programItemId,
	}),
	doWorkoutComplete: (programItemId: string, calories: number) => ({
		type: REQUEST_WORKOUT_COMPLETED,
		programItemId,
		calories,
	}),
	doReplaceWithCustomWorkout: (programItemId: string, workoutExternalId: string, duration: number, met: number) => ({
		type: REQUEST_WORKOUT_REPLACE_WITH_CUSTOM,
		programItemId,
		workoutExternalId,
		duration,
		met,
	}),
	doWorkoutCustomUpdate: (programItemId: string, workoutExternalId: string, duration: number, met: number) => ({
		type: REQUEST_WORKOUT_CUSTOM_UPDATED,
		programItemId,
		workoutExternalId,
		duration,
		met,
	}),

	doWorkoutCustomUpdateAndComplete: (
		programItemId: string,
		workoutExternalId: string,
		duration: number,
		met: number
	) => ({
		type: REQUEST_WORKOUT_CUSTOM_UPDATED_AND_COMPLETE,
		programItemId,
		workoutExternalId,
		duration,
		met,
	}),
	doCreateCustomFromOptional: (
		contentType: string, // atm. workout only
		programItemExternalId: string,
		exerciseExternalId: string,
		duration: number,
		met: number,
		dayNumber: number
	) => ({
		type: REQUEST_OPTIONAL_WORKOUT_CREATE_CUSTOM,
		contentType, // atm. workout only
		programItemExternalId,
		exerciseExternalId,
		duration,
		met,
		dayNumber,
	}),
	doCompleteSuccess: (programItem: ProgramItemType, currentDayNumber?: number) => ({
		type: REQUEST_COMPLETED_SUCCESS,
		programItem,
		currentDayNumber,
	}),
	doPostpone: (programItemId: string) => ({
		type: REQUEST_POSTPONE,
		programItemId,
	}),
	doPostponeSuccess: (programItemId: string) => ({
		type: REQUEST_POSTPONE_SUCCESS,
		programItemId,
	}),

	doHeart: (programItemId: string) => ({
		type: REQUEST_HEART,
		programItemId,
	}),
	doHeartSuccess: (programItemId: string) => ({
		type: REQUEST_HEART_SUCCESS,
		programItemId,
	}),
	doClose: (programItemId: string) => ({
		type: REQUEST_CLOSE,
		programItemId,
	}),
	doCloseSuccess: (programItem: ProgramItemType, currentDayNumber?: number) => ({
		type: REQUEST_CLOSE_SUCCESS,
		programItem,
		currentDayNumber,
	}),

	doSaveTracking: (programItemId: string, measurement: MeasurementType) => ({
		type: REQUEST_SAVE_TRACKING,
		programItemId,
		measurement,
	}),
	doSaveTrackingSuccess: (programItem: ProgramItemType, currentDayNumber?: number) => ({
		type: REQUEST_SAVE_TRACKING_SUCCESS,
		programItem,
		currentDayNumber,
	}),
	doUpdateTracking: (programItemId: string, measurement: MeasurementType) => ({
		type: REQUEST_UPDATE_TRACKING,
		programItemId,
		measurement,
	}),
	doUpdateTrackingSuccess: (programItem: ProgramItemType) => ({
		type: REQUEST_UPDATE_TRACKING_SUCCESS,
		programItem,
	}),
	doSlogan: (programItemId: string, slogan: string) => ({
		type: REQUEST_SLOGAN,
		programItemId,
		slogan,
	}),
	doSloganSuccess: (programItem: ProgramItemType, currentDayNumber?: number) => ({
		type: REQUEST_SLOGAN_SUCCESS,
		programItem,
		currentDayNumber,
	}),
	doToggleFavorite: ({
		id,
		favorite,
		type,
		externalId,
	}: {
		id: number;
		favorite: boolean;
		type: string;
		externalId?: string;
	}) => ({
		type: REQUEST_TOGGLE_FAVORITE,
		itemType: type,
		externalId,
		favorite,
		id,
	}),
	doToggleFavoriteSuccess: ({
		id,
		externalContentId,
		favorite,
		itemType,
	}: {
		id: number;
		externalContentId: any;
		favorite: boolean;
		itemType: string;
	}) => ({
		type: REQUEST_TOGGLE_FAVORITE_SUCCESS,
		externalContentId,
		favorite,
		id,
		itemType,
	}),
	doToggleFavoriteFailure: (id: number) => ({
		type: REQUEST_TOGGLE_FAVORITE_FAILURE,
		id,
	}),
	doSwapItem: (contentType: string, externalId: string, lastDayRef: number) => ({
		type: REQUEST_SWAP_ITEM,
		contentType,
		externalId,
		lastDayRef,
	}),
	doSwapItemSuccess: (externalId: string, contents: ProgramItemDtoType) => ({
		type: REQUEST_SWAP_ITEM_SUCCESS,
		externalId, // ID to replace in programItemsStore
		contents, // Card content
	}),
	doSwapItemFailure: (error: Error) => ({
		type: REQUEST_SWAP_ITEM_FAILURE,
		error,
	}),
	doMoveItem: (
		contentType: string,
		programItemExternalId: number,
		strategy: 'postpone' = 'postpone',
		days: number = 1
	) => ({
		type: REQUEST_MOVE_ITEM,
		contentType,
		programItemExternalId,
		strategy,
		days,
	}),
	doMoveItemSuccess: (
		programItemExternalId: number,
		strategy: 'postpone' = 'postpone',
		currentDayId: number,
		days: number = 1
	) => ({
		type: REQUEST_MOVE_ITEM_SUCCESS,
		currentDayId,
		programItemExternalId,
		strategy,
		days,
	}),
	doMoveItemFailure: (
		programItemExternalId: number,
		strategy: 'postpone' = 'postpone',
		days: number = 1,
		error?: Error
	) => ({
		type: REQUEST_MOVE_ITEM_FAILURE,
		programItemExternalId,
		strategy,
		days,
		error,
	}),
};

export type ProgramItemsActionType =
	| ReturnType<typeof actionCreators.doFlushProgramItems>
	| ReturnType<typeof actionCreators.doRequestProgramItemsContentsSuccess>
	| ReturnType<typeof actionCreators.doReplaceItem>
	| ReturnType<typeof actionCreators.doSetItemIsCustom>
	| ReturnType<typeof actionCreators.doReplaceItemContents>
	| ReturnType<typeof actionCreators.doComplete>
	| ReturnType<typeof actionCreators.doWorkoutComplete>
	| ReturnType<typeof actionCreators.doReplaceWithCustomWorkout>
	| ReturnType<typeof actionCreators.doWorkoutCustomUpdate>
	| ReturnType<typeof actionCreators.doCompleteSuccess>
	| ReturnType<typeof actionCreators.doPostpone>
	| ReturnType<typeof actionCreators.doPostponeSuccess>
	| ReturnType<typeof actionCreators.doHeart>
	| ReturnType<typeof actionCreators.doHeartSuccess>
	| ReturnType<typeof actionCreators.doClose>
	| ReturnType<typeof actionCreators.doCloseSuccess>
	| ReturnType<typeof actionCreators.doSaveTracking>
	| ReturnType<typeof actionCreators.doSaveTrackingSuccess>
	| ReturnType<typeof actionCreators.doUpdateTracking>
	| ReturnType<typeof actionCreators.doUpdateTrackingSuccess>
	| ReturnType<typeof actionCreators.doSlogan>
	| ReturnType<typeof actionCreators.doSloganSuccess>
	| ReturnType<typeof actionCreators.doToggleFavorite>
	| ReturnType<typeof actionCreators.doToggleFavoriteSuccess>
	| ReturnType<typeof actionCreators.doToggleFavoriteFailure>
	| ReturnType<typeof actionCreators.doSwapItem>
	| ReturnType<typeof actionCreators.doSwapItemSuccess>
	| ReturnType<typeof actionCreators.doSwapItemFailure>
	| ReturnType<typeof actionCreators.doMoveItem>
	| ReturnType<typeof actionCreators.doMoveItemSuccess>
	| ReturnType<typeof actionCreators.doMoveItemFailure>;
